import React, { useState, useEffect } from 'react';
import { FolderTree, Plus, Trash2, Edit2, ChevronDown, ChevronRight, Save, X } from 'lucide-react';
import { api } from '../../services/api';
import type { SAPMainCategory, SAPSubCategory } from '../../types';

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<SAPMainCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // New category form
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');

  // New subcategory form
  const [showNewSub, setShowNewSub] = useState<number | null>(null);
  const [newSubForm, setNewSubForm] = useState({ name: '', dataTable: '' });

  // Edit subcategory
  const [editingSub, setEditingSub] = useState<number | null>(null);
  const [editSubForm, setEditSubForm] = useState({ dataTable: '' });

  const loadCategories = async () => {
    try {
      const data = await api.getSAPCategories();
      if (data.success) setCategories(data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  const createMainCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post('/categories/main', { mainCategoryName: newCatName.trim() });
      setMessage({ type: 'success', text: `Category "${newCatName}" created!` });
      setNewCatName('');
      setShowNewCat(false);
      await loadCategories();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const deleteMainCategory = async (catId: number, catName: string) => {
    if (!window.confirm(`Delete "${catName}" and all its subcategories?`)) return;
    try {
      await api.delete(`/categories/main/${catId}`);
      setMessage({ type: 'success', text: `Category "${catName}" deleted.` });
      await loadCategories();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const createSubcategory = async (mainCatId: number) => {
    if (!newSubForm.name.trim()) return;
    try {
      const dataTable = newSubForm.dataTable.trim() || newSubForm.name.trim().replace(/\s+/g, '');
      await api.post('/categories/subcategory', {
        mainCategoryId: mainCatId,
        subCategoryName: newSubForm.name.trim(),
        dataTable,
      });
      setMessage({ type: 'success', text: `Subcategory "${newSubForm.name}" created!` });
      setNewSubForm({ name: '', dataTable: '' });
      setShowNewSub(null);
      await loadCategories();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const deleteSubcategory = async (subId: number, subName: string) => {
    if (!window.confirm(`Delete subcategory "${subName}"?`)) return;
    try {
      await api.delete(`/categories/subcategory/${subId}`);
      setMessage({ type: 'success', text: `Subcategory "${subName}" deleted.` });
      await loadCategories();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const updateSubDataTable = async (subId: number) => {
    if (!editSubForm.dataTable.trim()) return;
    try {
      await api.put(`/categories/subcategory/${subId}/datatable`, { dataTable: editSubForm.dataTable.trim() });
      setMessage({ type: 'success', text: 'Data table updated.' });
      setEditingSub(null);
      await loadCategories();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-auto">
      <div className="p-8 max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FolderTree className="w-6 h-6 text-blue-600" />
              Category Management
            </h1>
            <p className="text-gray-500 mt-1">Manage SAP data categories and subcategories</p>
          </div>
          <button onClick={() => setShowNewCat(!showNewCat)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> New Category
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* New Main Category Form */}
        {showNewCat && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 shadow-sm flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
              <input type="text" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Production Data" onKeyDown={e => e.key === 'Enter' && createMainCategory()} />
            </div>
            <button onClick={createMainCategory} disabled={!newCatName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300">Create</button>
            <button onClick={() => setShowNewCat(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        )}

        {/* Categories List */}
        <div className="space-y-3">
          {categories.map(cat => (
            <div key={cat.MainCategoryID} className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Category Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => setExpandedCat(expandedCat === cat.MainCategoryID ? null : cat.MainCategoryID)}>
                  {expandedCat === cat.MainCategoryID ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <FolderTree className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-semibold text-gray-900">{cat.MainCategoryName}</div>
                    <div className="text-xs text-gray-500">{(cat.SubCategories || []).length} subcategories</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setShowNewSub(showNewSub === cat.MainCategoryID ? null : cat.MainCategoryID); setExpandedCat(cat.MainCategoryID); }}
                    className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Sub
                  </button>
                  <button onClick={() => deleteMainCategory(cat.MainCategoryID, cat.MainCategoryName)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Delete category">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded: Subcategories */}
              {expandedCat === cat.MainCategoryID && (
                <div className="border-t border-gray-100 p-4">
                  {/* New Subcategory Form */}
                  {showNewSub === cat.MainCategoryID && (
                    <div className="bg-blue-50 rounded-lg p-3 mb-3 flex gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Subcategory Name</label>
                        <input type="text" value={newSubForm.name} onChange={e => setNewSubForm(f => ({ ...f, name: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="e.g., General Info" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Data Table Name</label>
                        <input type="text" value={newSubForm.dataTable} onChange={e => setNewSubForm(f => ({ ...f, dataTable: e.target.value }))}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="Auto-generated if empty" />
                      </div>
                      <button onClick={() => createSubcategory(cat.MainCategoryID)} disabled={!newSubForm.name.trim()}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:bg-blue-300">Add</button>
                      <button onClick={() => setShowNewSub(null)} className="p-1.5 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                    </div>
                  )}

                  {/* Subcategories Table */}
                  {(cat.SubCategories || []).length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                          <th className="pb-2 font-medium">Subcategory</th>
                          <th className="pb-2 font-medium">Data Table</th>
                          <th className="pb-2 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(cat.SubCategories || []).map(sub => (
                          <tr key={sub.SubCategoryID} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 font-medium text-gray-900">{sub.SubCategoryName}</td>
                            <td className="py-2">
                              {editingSub === sub.SubCategoryID ? (
                                <div className="flex gap-1">
                                  <input type="text" value={editSubForm.dataTable}
                                    onChange={e => setEditSubForm({ dataTable: e.target.value })}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32" />
                                  <button onClick={() => updateSubDataTable(sub.SubCategoryID)}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded"><Save className="w-3 h-3" /></button>
                                  <button onClick={() => setEditingSub(null)}
                                    className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X className="w-3 h-3" /></button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs font-mono">{sub.Data_Table || '—'}</span>
                              )}
                            </td>
                            <td className="py-2 text-right">
                              <button onClick={() => { setEditingSub(sub.SubCategoryID); setEditSubForm({ dataTable: sub.Data_Table || '' }); }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit data table">
                                <Edit2 className="w-3 h-3" />
                              </button>
                              <button onClick={() => deleteSubcategory(sub.SubCategoryID, sub.SubCategoryName)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ml-1" title="Delete">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No subcategories yet. Click "+ Sub" to add one.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;
